import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  useAncestorShares,
  useCreateFolder,
  useCreateProject,
  useFolderAncestors,
  useFolderShares,
  useInheritedRecursiveShares,
  NOT_OWNED_ANCESTOR_ERROR,
  type AncestorSharesResult,
} from "src/api/projectFolders/projectFolderService";
import { useGetUserDetails } from "src/helpers/UserDetails";

import type { ProjectFolder } from "src/api/projectFolders/types";
import "src/features/library/dialogs/library-dialog.scss";

interface CreateFolderDialogProps {
  open: boolean;
  onClose: () => void;
  // Without a parent this dialog creates a project (a root folder) via the
  // dedicated create-project endpoint; plain folder-create requires a parent.
  parentId?: number;
  // Hands the created folder to callers that act on it (e.g. add-to-project
  // picking the fresh project as its destination). Called before onClose.
  onCreated?: (folder: ProjectFolder) => void;
}

const CreateFolderDialog: React.FC<CreateFolderDialogProps> = ({
  open,
  onClose,
  parentId,
  onCreated,
}) => {
  const [name, setName] = useState("");
  const createFolderMutation = useCreateFolder();
  const createProjectMutation = useCreateProject();
  const isProject = parentId === undefined;
  const { isLoading, isError } = isProject
    ? createProjectMutation
    : createFolderMutation;
  const resetFolder = createFolderMutation.reset;
  const resetProject = createProjectMutation.reset;

  // Story 13 AC 17: creating a folder inside a shared project says who will
  // be able to read what goes into it. Readers of the new folder are the
  // subjects whose grants reach a new child: recursive grants on the parent
  // or any ancestor. Non-recursive grants stop at the folder they sit on.
  const currentUser = useGetUserDetails();
  const sharesId = open && !isProject ? (parentId ?? null) : null;
  const {
    data: parentShares,
    error: parentSharesError,
    isLoading: parentSharesLoading,
  } = useFolderShares(sharesId);

  // The backend never 404s for a permission shortfall (that's a 403), so a
  // 404 on the parent means the parent itself is missing (deleted out from
  // under this dialog) — asserting anything about who can read a folder
  // that no longer exists would be its own false claim, so that case
  // suppresses the whole disclosure below (the create-folder submit will
  // surface its own failure instead). Computed before the ancestor fetches
  // below so they can skip the N-request fan-out once it's known: none of
  // it will be used either way.
  const parentMissing =
    axios.isAxiosError(parentSharesError) &&
    parentSharesError.response?.status === 404;

  const {
    data: ancestors,
    error: ancestorsError,
    isLoading: ancestorsLoading,
  } = useFolderAncestors(sharesId);
  const ancestorShares = useAncestorShares(
    ancestors,
    sharesId !== null && !parentMissing,
  );

  // The parent reaches a new child exactly like a recursive ancestor grant
  // does (spec §5.3: only recursive reaches down) — a non-recursive grant on
  // the parent stops at the parent itself. Prepending it as a pseudo-ancestor
  // lets both feed the one shared recursive-filter (#2461) instead of the
  // parent re-deriving the same rule inline; its `name` is never rendered.
  const parentAsAncestor: AncestorSharesResult = useMemo(
    () => ({
      ancestor: { id: sharesId ?? -1, name: "" },
      shares: parentShares,
      error: parentSharesError,
      isLoading: parentSharesLoading,
    }),
    [sharesId, parentShares, parentSharesError, parentSharesLoading],
  );
  // Memoized so useInheritedRecursiveShares' own memo (keyed on this array's
  // identity) actually hits — ancestorShares is now stable across renders
  // that don't change its inputs, but a fresh `[...]` here every render
  // would still defeat that.
  const combinedAncestorShares = useMemo(
    () => (sharesId !== null ? [parentAsAncestor, ...ancestorShares] : []),
    [sharesId, parentAsAncestor, ancestorShares],
  );
  const recursiveShares = useInheritedRecursiveShares(combinedAncestorShares);

  const readerNames = useMemo(() => {
    const users = new Map<number, string>();
    const groups = new Map<number, string>();
    for (const { share } of recursiveShares.userShares) {
      if (share.user_id !== currentUser?.id) {
        // A grant can outlive its user (their relation goes null server
        // side): name and email both resolve empty, which would otherwise
        // land a blank label in the list ("readable by , Bob").
        const label = share.name || share.email || `User #${share.user_id}`;
        users.set(share.user_id, label);
      }
    }
    for (const { share } of recursiveShares.groupShares) {
      groups.set(share.group_id, share.group_name);
    }
    return [
      ...users.values(),
      ...[...groups.values()].map((name) => `${name} (group)`),
    ];
  }, [recursiveShares, currentUser]);

  // Any other failure on the parent's own share list — 403 (the expected
  // non-owner outcome), 500, a network drop, anything — means the grants
  // made directly on the parent are invisible to readerNames, the same as
  // an ancestor failing below. It doesn't get to stay narrower than that
  // check just because it usually 403s.
  const sharesForbidden = parentSharesError != null && !parentMissing;

  // Same effect on an ancestor as sharesForbidden above, but for ANY failure
  // — not just the expected 403. useAncestorShares drops that ancestor's
  // recursive grants whenever its query errors (retry: false), whatever the
  // status: a 500, a network failure, or any other AxiosError under-reports
  // exactly like a 403 does. A disclosure that is confidently wrong is worse
  // than none, so any such gap forces the caveat/generic wording below
  // rather than the bare list. The ancestors list itself failing is worse
  // still — it silences every ancestor's grants at once, not just one — so
  // it counts here too.
  const ancestorSharesForbidden =
    ancestorsError != null || ancestorShares.some((r) => r.error != null);

  // Withhold the named list (and the generic fallback) until every relevant
  // share query has settled. Rendering from whatever has resolved so far —
  // an ancestor still in flight, or the ancestor list itself not loaded yet
  // (so we don't even know how many ancestors could still contribute a
  // name) — is the same "confidently wrong" failure sharesForbidden and
  // ancestorSharesForbidden exist to avoid; it just happens on a render
  // that will correct itself a moment later instead of staying wrong.
  const disclosurePending =
    parentSharesLoading ||
    ancestorsLoading ||
    ancestorShares.some((r) => r.isLoading);

  // Either failure means readerNames may be missing a subject, whatever
  // list it did manage to assemble — grounds for the caveat appended to a
  // non-empty list, but NOT grounds on its own for the affirmative "this
  // folder is shared" claim below: a transient 500 proves nothing about
  // whether the folder is shared with anyone, and neither does an ancestor
  // whose permission_level was never resolved (absence of evidence, not
  // evidence of absence). Only confirmed non-ownership does — the parent's
  // own live 403 (the shares endpoint is owner-only, so a non-owner
  // reaching this dialog at all, which needs only write, must hold that
  // write through someone else's share), or an ancestor's
  // NOT_OWNED_ANCESTOR_ERROR specifically, checked by reference so a real
  // failure from the batch endpoint (which can also carry status 403)
  // doesn't get mistaken for it.
  const disclosureIncomplete = sharesForbidden || ancestorSharesForbidden;
  const confirmedShared =
    (axios.isAxiosError(parentSharesError) &&
      parentSharesError.response?.status === 403) ||
    ancestorShares.some((r) => r.error === NOT_OWNED_ANCESTOR_ERROR);

  // A missing parent makes the whole disclosure moot, not just the generic
  // fallback: nothing here — pending, named, or generic — should render
  // about a folder that's confirmed gone.
  const showDisclosure = !isProject && !parentMissing;

  useEffect(() => {
    if (!open) {
      setName("");
      resetFolder();
      resetProject();
    }
  }, [open, resetFolder, resetProject]);

  const handleSubmit = () => {
    if (!name.trim()) return;

    const handleSuccess = (folder: ProjectFolder) => {
      onCreated?.(folder);
      onClose();
    };
    if (parentId !== undefined) {
      createFolderMutation.mutate(
        { name: name.trim(), parent_id: parentId },
        { onSuccess: handleSuccess },
      );
    } else {
      createProjectMutation.mutate(
        { name: name.trim() },
        { onSuccess: handleSuccess },
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && name.trim() && !isLoading) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="library-dialog"
      data-testid="create-folder-dialog"
      data-attr="folder-browser:modal:create-folder"
    >
      <DialogTitle>
        {isProject ? "Create Project" : "Create New Folder"}
      </DialogTitle>
      <DialogContent>
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to create {isProject ? "project" : "folder"}. Please try
            again.
          </Alert>
        )}
        <TextField
          autoFocus
          margin="dense"
          label={isProject ? "Project Name" : "Folder Name"}
          fullWidth
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          data-testid="create-folder-name-input"
          data-attr="folder-browser:input:new-folder-name"
        />
        {showDisclosure && disclosurePending && (
          <Alert
            severity="info"
            icon={false}
            sx={{ mt: 2 }}
            data-testid="create-folder-readers-loading"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="body2">
                Checking who can read this folder…
              </Typography>
            </Stack>
          </Alert>
        )}
        {showDisclosure && !disclosurePending && readerNames.length > 0 && (
          <Alert
            severity="info"
            sx={{ mt: 2 }}
            data-testid="create-folder-readers"
          >
            Anything added to this folder will be readable by{" "}
            {readerNames.join(", ")}
            {disclosureIncomplete && ", and possibly others we can't verify"}.
          </Alert>
        )}
        {showDisclosure &&
          !disclosurePending &&
          readerNames.length === 0 &&
          confirmedShared && (
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              data-testid="create-folder-readers-generic"
            >
              This folder is inside a shared project. Everyone who can read the
              project will be able to read what goes into it.
            </Alert>
          )}
        {showDisclosure &&
          !disclosurePending &&
          readerNames.length === 0 &&
          disclosureIncomplete &&
          !confirmedShared && (
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              data-testid="create-folder-readers-unknown"
            >
              Couldn't determine who else can read this folder.
            </Alert>
          )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isLoading}
          data-testid="create-folder-cancel-btn"
          data-attr="folder-browser:btn:cancel-create-folder"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!name.trim() || isLoading}
          data-testid="create-folder-confirm-btn"
          data-attr="folder-browser:btn:confirm-create-folder"
        >
          {isLoading && (
            <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
          )}
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFolderDialog;
